import type {
  CreateMultiLegOrderRequest,
  CreateOrderRequest,
} from "~/v2/api/rest/order";
import { sideForQuoteSide } from "../../shared/optionLegs/optionLeg";
import type { OrderLeg } from "../../shared/optionLegs/optionLeg.types";
import type {
  MultiLegOrderReviewSnapshot,
  OrderReviewSnapshot,
  SingleOrderReviewSnapshot,
  TicketMode,
  TicketOrderType,
  TicketSide,
  TicketTimeInForce,
} from "./OrderTicketWidget.types";

interface CreateOrderReviewSnapshotInput {
  mode: Exclude<TicketMode, "empty">;
  displaySymbol: string;
  orderSymbol: string | undefined;
  side: TicketSide;
  quantity: number;
  orderType: TicketOrderType;
  limitPrice: number | undefined;
  timeInForce: TicketTimeInForce;
  estimatedCost: number | undefined;
  legs: OrderLeg[];
}

export const createOrderReviewSnapshot = ({
  mode,
  displaySymbol,
  orderSymbol,
  side,
  quantity,
  orderType,
  limitPrice,
  timeInForce,
  estimatedCost,
  legs,
}: CreateOrderReviewSnapshotInput): OrderReviewSnapshot => {
  const price = orderType === "limit" ? { limitPrice } : {};

  if (mode === "multileg") {
    return {
      mode,
      displaySymbol,
      quantity,
      orderType,
      ...price,
      timeInForce: timeInForce === "gtc" ? "gtc" : "day",
      legs: legs.map((leg) => ({
        symbol: leg.contract.symbol,
        side: sideForQuoteSide(leg.quoteSide),
        ratioQty: "1",
      })),
    };
  }

  if (!orderSymbol) {
    throw new Error("A single order requires a symbol");
  }

  return {
    mode,
    displaySymbol,
    orderSymbol,
    side,
    quantity,
    orderType,
    ...price,
    timeInForce,
    estimatedCost,
  };
};

export const createSingleOrderRequest = (
  snapshot: SingleOrderReviewSnapshot,
): CreateOrderRequest => ({
  symbol: snapshot.orderSymbol,
  side: snapshot.side,
  type: snapshot.orderType,
  time_in_force: snapshot.timeInForce,
  qty: String(snapshot.quantity),
  ...(snapshot.orderType === "limit" && snapshot.limitPrice !== undefined
    ? { limit_price: String(snapshot.limitPrice) }
    : {}),
});

export const createMultiLegOrderRequest = (
  snapshot: MultiLegOrderReviewSnapshot,
): CreateMultiLegOrderRequest => ({
  type: snapshot.orderType,
  time_in_force: snapshot.timeInForce,
  qty: String(snapshot.quantity),
  ...(snapshot.orderType === "limit" && snapshot.limitPrice !== undefined
    ? { limit_price: String(snapshot.limitPrice) }
    : {}),
  legs: snapshot.legs.map((leg) => ({
    symbol: leg.symbol,
    side: leg.side,
    ratio_qty: leg.ratioQty,
  })),
});
